import { ArrowLinePoints } from '@components/ArtBoard/Arrows/components/ArrowLine/components/ArrowLinePoints'
import { CollaborationPointers } from '@components/ArtBoard/Collaboration/components/CollaborationPointers/CollaborationPointers'
import { ExternalLinks } from '@components/ArtBoard/ExternalLinks'
import { StickyNotes } from '@components/ArtBoard/StickyNotes'
import { AttachmentCollection } from '@components/ArtBoard/TabAttachments'
import { COMMENTABLE_TARGET, Threads } from '@components/ArtBoard/Threads'
import {
  type Attachment,
  selectTabAttachments,
} from '@octopus-do/octopus-project-core'
import { migrationArtBoardSelectors } from '@store/migrationSelectors/artBoardSelectors'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import classNames from 'classnames'
import type React from 'react'
import { useEffect, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useArtboardMode } from '@/hooks'
import { GlobalActions } from '@/store'
import { Legend } from '../Legend'
import Sections from '../Sections/Sections'
import css from './Tree.module.scss'

const Tree: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)

  const dispatch = useDispatch()

  const { seoMode, simpleView, gallery } = useArtboardMode()
  const showExtra = !(seoMode || simpleView || gallery)

  const treeVariant = useSelector(
    migrationProjectSelector.getProjectVariantType
  )

  const legendMode = useSelector(
    migrationProjectSelector.getProjectLegendPosition
  )

  const isEmbed = useSelector(migrationArtBoardSelectors.getIsEmbed)

  useEffect(() => {
    if (!isEmbed || !ref.current) {
      return
    }

    dispatch({
      type: CanvasActionTypes.SET_SCALE,
      payload: {
        scale: 0.45,
      },
    })

    dispatch(
      GlobalActions.updateCanvas({
        scale: 0.45,
      })
    )

    const rect = ref.current.getBoundingClientRect()

    if (rect.y > 90) {
      dispatch(
        GlobalActions.updateCanvas({
          translate: [0, -rect.y + 36],
        })
      )
    }
  }, [ref])

  const activeTab = useSelector(migrationProjectSelector.getActiveOrFirstTabId)

  const attachments = useSelector<AppState, Attachment[]>(
    (state) =>
      selectTabAttachments(activeTab)({
        project: state.manager.project,
        relations: state.relations,
      }),
    shallowEqual
  )

  return (
    <div
      id="tree"
      style={{ transform: 'scale(1)' }}
      className={classNames(['Tree-root', 'is-tree', `is-${treeVariant}`])}
      ref={ref}
      {...{ [COMMENTABLE_TARGET]: activeTab }}
    >
      {showExtra && legendMode === 'top' && <Legend />}

      <div className={css.rootContainer}>
        <Sections />

        {!seoMode && <AttachmentCollection attachments={attachments} />}

        {!seoMode && <StickyNotes parentId={activeTab} anchor="tab" />}

        {!seoMode && <ExternalLinks />}

        <CollaborationPointers />
      </div>

      {/* both layers hold coordinates measured from #tree — the thread offset
          from the element carrying the commentable target, the arrow points
          from .Tree-root — so they are anchored to #tree as well. Inside
          .rootContainer a top legend pushes every one of them down by its
          height. */}
      {showExtra && <ArrowLinePoints />}

      {!seoMode && <Threads target={activeTab} />}

      {showExtra && legendMode === 'bottom' && <Legend />}
    </div>
  )
}

export default Tree
